'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { formatDistance } from 'date-fns';
import clsx from 'clsx';
import { WalkerSectionContainer } from '../WalkerServices/WalkerCollapsible/WalkerSectionContainer';
import { WalkerActivityV6, WalkerAvailabilityV6, WalkerOfferedServicesResponse, WalkerProfileService, WalkerProfileV6 } from '../../../../types/api';
import { WalkerCalendar } from './WalkerCalendar';
import { useGetWalkerAvailability } from '../../../../hooks/api/getWalkerAvailability';
import { formatAvailabilityDate } from '../../../../utils/formatAvailabilityDate';
import { filterDisabledDays } from './helpers';
import { DirectBookingButton } from '../../../../components/BrowseAndBookProfile/components/DirectBookingButton';
import { useGetWalkerActivity } from '../../../../hooks/api/useGetWalkerActivity/getWalkerActivity';
import { useRollbar } from '../../../../hooks';
import { transformServicesOffered, WalkerRates } from './helpers/transformServicesOffered';
import { BoxProps } from '../../../../wag-react/next-components/box';
import { Box } from '../../../../wag-react/core-components/box';
import { Typography } from '../../../../wag-react/components/Typography';
import { theme } from '../../../../wag-react/themes/base-theme';
import { WAGWEB_BOOKING_URL } from '../../../../constants';
type Props = {
  availability: WalkerAvailabilityV6;
  servicesOffered: WalkerOfferedServicesResponse;
  profile: WalkerProfileV6;
  identifier: string;
  queryParams: Record<string, string>;
  containerProps?: BoxProps;
};
export const WalkerSchedule = ({
  containerProps,
  profile,
  identifier,
  servicesOffered,
  availability: initialAvailability,
  queryParams
}: Props) => {
  const [walkerActivity, setWalkerActivity] = useState<WalkerActivityV6>();
  const [transformedServices, setTransformedServices] = useState<WalkerProfileService[]>([]);
  const [transformedRates, setTransformedRates] = useState<WalkerRates>();
  const [directBookingUrl, setDirectBookingUrl] = useState<string>('');
  const [isDirectBookingModalOpen, setIsDirectBookingModalOpen] = useState(false);
  const [disabledDays, setDisabledDays] = useState<Date[]>(filterDisabledDays({
    availability: initialAvailability.availability
  }));
  const rollbar = useRollbar();
  const {
    getActivity,
    error: activityError
  } = useGetWalkerActivity(identifier);
  const getWalkerActivity = useCallback(async () => {
    const activity = await getActivity();
    if (activityError) {
      rollbar.error(JSON.stringify(error));
    }
    setWalkerActivity(activity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    getWalkerActivity();
  }, [getWalkerActivity]);
  const {
    error,
    getAvailability,
    isLoading
  } = useGetWalkerAvailability(identifier);
  if (servicesOffered?.items?.length && !transformedServices.length && !transformedRates) {
    const {
      reducedRates,
      reducedServices
    } = transformServicesOffered({
      servicesOffered
    });
    setTransformedServices(reducedServices);
    setTransformedRates(reducedRates);
  }
  useEffect(() => {
    if (isDirectBookingModalOpen) {
      window.open(directBookingUrl, '_self');
    }
  }, [isDirectBookingModalOpen, directBookingUrl]);
  useEffect(() => {
    const searchBanner = document.querySelector('#book-now-button') as HTMLDivElement;
    const handleScroll = () => {
      const currentScroll = window.pageYOffset;
      if (currentScroll > 300 && currentScroll < window.innerHeight * 1.8) {
        searchBanner.style.transform = `translate3d(0, 0, 0)`;
        return;
      }
      searchBanner.style.transform = `translate3d(0, 168px, 0)`;
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, false);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return <Box bg="white" borderRadius="16px" overflow="hidden" className="shadow-md" py={3} px={1} my={['30px', 0]} {...containerProps} data-sentry-element="Box" data-sentry-component="WalkerSchedule" data-sentry-source-file="index.tsx">
      <WalkerSectionContainer title="Book this caregiver" text={profile.tags.find(tag => tag.label === 'High Demand') ? 'Be quick! This caregiver is in high demand.' : null} containerProps={{
      p: 0,
      justifyContent: 'center',
      style: {
        rowGap: 0
      }
    }} titleProps={{
      pb: 1
    }} data-sentry-element="WalkerSectionContainer" data-sentry-source-file="index.tsx" />
      <WalkerCalendar calendarProps={{
      mode: 'single',
      className: clsx('animate-opacity', isLoading ? 'opacity-50' : 'opacity-100'),
      disabled: [...disabledDays, {
        before: new Date()
      }],
      onMonthChange: async date => {
        const availability: WalkerAvailabilityV6 = await getAvailability({
          startDate: formatAvailabilityDate({
            initialDate: date
          }),
          endDate: formatAvailabilityDate({
            initialDate: date,
            lastDay: true
          })
        });
        if (error) {
          rollbar.error(JSON.stringify(error));
          return;
        }
        setDisabledDays(filterDisabledDays({
          availability: availability.availability,
          startingDate: date
        }));
      }
    }} data-sentry-element="WalkerCalendar" data-sentry-source-file="index.tsx" />
      {error ? <Typography variant="legal" m={0} mb={3} mt={1} fontFamily="muliSemiBold" textAlign="center" color={theme.colors.red} fontSize="12px">
          {JSON.stringify(error)}
        </Typography> : <Typography m={0} mb={3} mt={1} fontFamily="muliSemiBold" fontSize="12px" textAlign="center" color="#272727">
          Calendar last updated:
          {' '}
          {formatDistance(new Date(walkerActivity?.lastUpdatedAt || new Date()), new Date())}
          {' '}
          ago
        </Typography>}
      <DirectBookingButton availability={initialAvailability} name={`${profile?.firstName} ${profile?.lastName}`} uuid={profile.uuid} thumbnail={profile.photo} baseUrl={WAGWEB_BOOKING_URL} setIsDirectBookingModalOpen={setIsDirectBookingModalOpen} services={transformedServices} rates={transformedRates} testId={2} promoCode={identifier} queryParamsFromServer={queryParams} setDirectBookingUrl={setDirectBookingUrl} walkerProfileV6 data-sentry-element="DirectBookingButton" data-sentry-source-file="index.tsx" />
    </Box>;
};